import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import _ from 'lodash';
import { RealtimeChannel } from '@supabase/supabase-js';
import debounce from 'lodash.debounce';
import { getSupabaseClient } from '../lib/supabase';

// Using temporary types to unblock development
type ExcalidrawElement = any;
type AppState = any;

// Self-contained types
export type RoomId = string;
export type UserId = string;

export interface BoardData {
  elements: ExcalidrawElement[];
  appState: Partial<AppState>;
}

export interface DrawingRoomState {
  isLoading: boolean;
  error: Error | null;
  roomId: RoomId | null;
  userId: UserId | null;
  userName: string | null;
  elements: ExcalidrawElement[];
  appState: AppState;
  isConnected: boolean;
  version: string | null;
  isBoardInitialized: boolean;
}

export interface DrawingRoomContextType extends DrawingRoomState {
  joinRoom: (roomId: RoomId, userId: UserId, userName: string) => Promise<void>;
  leaveRoom: () => void;
  handleLocalChange: (
    elements: readonly ExcalidrawElement[],
    appState: AppState
  ) => void;
}

const DrawingRoomContext = createContext<DrawingRoomContextType | null>(null);

type Action =
  | { type: 'JOIN_ROOM'; payload: { roomId: RoomId; userId: UserId; userName: string } }
  | { type: 'LEAVE_ROOM' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_BOARD_DATA'; payload: BoardData & { version: string } }
  | { type: 'SET_ELEMENTS'; payload: ExcalidrawElement[] }
  | { type: 'SET_APP_STATE'; payload: Partial<AppState> }
  | { type: 'SET_ERROR'; payload: Error | null }
  | { type: 'SET_BOARD_INITIALIZED'; payload: boolean };

const initialState: DrawingRoomState = {
  isLoading: true,
  error: null,
  roomId: null,
  userId: null,
  userName: null,
  elements: [],
  appState: {
    viewBackgroundColor: '#ffffff',
    currentItemFontFamily: 1,
    collaborators: new Map(),
  },
  isConnected: false,
  version: null,
  isBoardInitialized: false,
};

const drawingRoomReducer = (state: DrawingRoomState, action: Action): DrawingRoomState => {
  console.log(`[Reducer] Action: ${action.type}`, 'payload' in action ? action.payload : '');
  switch (action.type) {
    case 'JOIN_ROOM':
      return {
        ...initialState,
        roomId: action.payload.roomId,
        userId: action.payload.userId,
        userName: action.payload.userName,
        isLoading: true,
      };
    case 'LEAVE_ROOM':
      return initialState;
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_BOARD_DATA':
      return {
        ...state,
        elements: action.payload.elements || [],
        appState: { ...state.appState, ...(action.payload.appState || {}) },
        isLoading: false,
        isConnected: true,
        version: action.payload.version,
      };
    case 'SET_ELEMENTS':
      return { ...state, elements: action.payload };
    case 'SET_APP_STATE':
      return { ...state, appState: { ...state.appState, ...action.payload } };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_BOARD_INITIALIZED':
      return { ...state, isBoardInitialized: action.payload };
    default:
      return state;
  }
};

export const DrawingRoomProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(drawingRoomReducer, initialState);
  const stateRef = useRef(state);
  const supabaseChannelRef = useRef<RealtimeChannel | null>(null);
  const isInteractingRef = useRef(false);
  const interactionTimerRef = useRef<NodeJS.Timeout | null>(null);
  stateRef.current = state;

  // --- FEEDBACK LOOP FIX ---
  const isRemoteUpdate = useRef(false);

  const localBoardDataRef = useRef<BoardData>({
    elements: state.elements,
    appState: state.appState,
  });

  useEffect(() => {
    localBoardDataRef.current = {
      elements: state.elements,
      appState: state.appState,
    };
  }, [state.elements, state.appState]);

  const supabase = getSupabaseClient();

  const sanitizeAppState = useCallback((appState: AppState) => {
    // Omitting UI-specific state that shouldn't be synced
    const { contextMenu, collaborators, viewMode, ...rest } = appState;
    return rest;
  }, []);

  const broadcastSceneUpdate = useMemo(
    () =>
      debounce(
        async () => {
          // Skip if this is a remote update to prevent feedback loops
          if (isRemoteUpdate.current) {
            console.log('[Broadcast] Skipping broadcast - remote update in progress');
            return;
          }
          
          // Use stateRef to get the latest state without re-creating the function
          if (!stateRef.current.roomId || !stateRef.current.userId || !localBoardDataRef.current) {
            console.log('[Broadcast] No room, user, or data to broadcast.');
            return;
          }

          const elements = localBoardDataRef.current.elements;
          const appState = sanitizeAppState(localBoardDataRef.current.appState);

          // Skip empty or invalid updates
          if (!elements || !Array.isArray(elements)) {
            console.log('[Broadcast] No valid elements to broadcast');
            return;
          }

          const payload = {
            version: new Date().toISOString(),
            elements: elements,
            appState: appState,
          };

          console.log(`[Broadcast] Broadcasting scene update for room: ${stateRef.current.roomId}`, {
            elementCount: elements.length,
            appStateKeys: Object.keys(appState)
          });

          try {
            const { error } = await supabase.from('board_events').insert({
              room_id: stateRef.current.roomId,
              user_id: stateRef.current.userId,
              event_type: 'SCENE_UPDATE',
              payload: payload,
            });

            if (error) {
              console.error('Failed to broadcast scene update:', error);
              // Optionally implement retry logic here
            } else {
              console.log('[Broadcast] Successfully broadcast scene update');
            }
          } catch (error) {
            console.error('Error in broadcastSceneUpdate:', error);
          } finally {
            // Ensure we reset the remote update flag if needed
            isRemoteUpdate.current = false;
          }
        },
        50, // Reduced debounce time for more responsive drawing
        { leading: false, trailing: true, maxWait: 100 }
      ),
    [supabase, sanitizeAppState]
  );

  const leaveRoom = useCallback(async () => {
    if (supabaseChannelRef.current) {
      try {
        await supabase.removeChannel(supabaseChannelRef.current);
        console.log('[Realtime] Unsubscribing from channel.');
        supabaseChannelRef.current = null;
      } catch (error) {
        console.error('Error unsubscribing from channel:', error);
      }
    }
    // Clear the interaction timer on unmount
    if (interactionTimerRef.current) {
      clearTimeout(interactionTimerRef.current);
    }
    dispatch({ type: 'LEAVE_ROOM' });
  }, []);

  const joinRoom = useCallback(
    async (roomId: RoomId, userId: UserId, userName: string) => {
      // Clean up any existing subscription
      if (supabaseChannelRef.current) {
        try {
          await supabase.removeChannel(supabaseChannelRef.current);
          console.log('[Realtime] Unsubscribing from previous channel.');
          supabaseChannelRef.current = null;
        } catch (error) {
          console.error('Error unsubscribing from previous channel:', error);
        }
      }
      
      // Set loading state before any async operations
      dispatch({ type: 'JOIN_ROOM', payload: { roomId, userId, userName } });

      try {
        // First, ensure board exists
        const { error: boardError } = await supabase
          .from('boards')
          .upsert({ id: roomId }, { onConflict: 'id' });
        
        if (boardError) throw boardError;


        
        // Then fetch the latest state
        const { data: lastEvent, error: lastEventError } = await supabase
          .from('board_events')
          .select('payload, created_at')
          .eq('room_id', roomId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (lastEventError && lastEventError.code !== 'PGRST116') {
          throw lastEventError;
        }

        let initialBoardState: BoardData | null = null;
        if (lastEvent) {
          const payload = lastEvent.payload as BoardData;
          console.log('[joinRoom] Loaded existing board state', {
            elements: payload.elements?.length,
            version: lastEvent.created_at,
          });
          // prevent feedback loop while applying initial state
          isRemoteUpdate.current = true;
          initialBoardState = payload;
          dispatch({
            type: 'SET_BOARD_DATA',
            payload: {
              ...initialBoardState,
              version: lastEvent.created_at as string,
            },
          });
          // Allow local broadcasts after initial sync
          setTimeout(() => {
            isRemoteUpdate.current = false;
          }, 0);
        } else {
          console.log('[joinRoom] No existing board state, initializing empty board');
          initialBoardState = {
            elements: [],
            appState: { viewBackgroundColor: '#fafafa' },
          };
          // ensure remote flag cleared
          isRemoteUpdate.current = false;
          const newVersion = new Date().toISOString();
          dispatch({
            type: 'SET_BOARD_DATA',
            payload: {
              ...initialBoardState,
              version: newVersion,
            },
          });
          // Persist initial empty state so other tabs load something
          try {
            const { error: saveErr } = await supabase.from('board_events').insert({
              room_id: roomId,
              user_id: userId,
              event_type: 'BOARD_CREATE',
              payload: initialBoardState,
              created_at: newVersion,
            });
            if (saveErr) {
              console.error('[joinRoom] Failed to save initial empty state', saveErr);
            }
          } catch (saveErr) {
            console.error('[joinRoom] Exception saving initial empty state', saveErr);
          }
        }

        // Board is ready
        dispatch({ type: 'SET_BOARD_INITIALIZED', payload: true });
      } catch (error: any) {
        console.error('Failed to fetch initial board state:', error);
        dispatch({ type: 'SET_ERROR', payload: error });
      }

      try {
        // Create a new channel
        const channel = supabase.channel(`board-events:${roomId}`, {
          config: {
            presence: {
              key: `board:${roomId}`,
            },
          },
        });

        // Set up the channel
        channel
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'board_events',
              filter: `room_id=eq.${roomId}`,
            },
            (payload: any) => {
              try {
                const event = payload.new;
                if (!event) return;

                console.log('[Realtime] Received remote event:', {
                  eventType: payload.eventType,
                  commit_timestamp: payload.commit_timestamp,
                  record: { ...event, payload: '...omitted...' }
                });

                const eventUserId = event.user_id;
                const isCurrentUser = eventUserId === stateRef.current.userId;
                const isInitialSync = !stateRef.current.version;

                // Skip only if this is our own update
                if (isCurrentUser) {
                  console.log('[Realtime] Skipping own event');
                  return;
                }

                // If we're in initial sync but have received a version, update our state
                if (isInitialSync) {
                  console.log('[Realtime] Processing initial sync update');
                }

                // If the user is actively interacting and we're not in initial sync, queue the update
                if (isInteractingRef.current && !isInitialSync) {
                  console.log('[Realtime] User is interacting, queuing update');
                  // Will be handled by the interaction timer
                  return;
                }

                const newBoardData = event.payload as BoardData;
                const version = payload.commit_timestamp as string;
                
                // Always update if we're in initial sync, otherwise check version
                if (isInitialSync || !stateRef.current.version || version > stateRef.current.version) {
                  console.log(`[Realtime] Applying update v${version}`);
                  
                  // Mark this as a remote update to prevent feedback loop
                  isRemoteUpdate.current = true;
                  
                  // Use requestAnimationFrame to ensure state updates are batched
                  requestAnimationFrame(() => {
                    dispatch({
                      type: 'SET_BOARD_DATA',
                      payload: { 
                        ...newBoardData, 
                        version 
                      },
                    });
                    
                    // Reset the flag after a small delay to ensure all updates are processed
                    setTimeout(() => {
                      isRemoteUpdate.current = false;
                    }, 100);
                  });
                } else {
                  console.log(`[Realtime] Skipping older update v${version} (current: v${stateRef.current.version})`);
                }
              } catch (error) {
                console.error('[Realtime] Error processing update:', error);
              }
            }
          )
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              console.log(`Successfully subscribed to events for room: ${roomId}`);
              supabaseChannelRef.current = channel; // Only store the channel ref after successful subscription
              dispatch({ type: 'SET_LOADING', payload: false });
            } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              console.error(`Channel error: ${status}`);
              supabaseChannelRef.current = null;
            }
          });
      } catch (error) {
        console.error('Error setting up channel:', error);
        dispatch({ type: 'SET_ERROR', payload: error as Error });
      }
    },
    [supabase]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const cleanup = async () => {
        if (supabaseChannelRef.current) {
          try {
            await supabase.removeChannel(supabaseChannelRef.current);
            console.log('[Realtime] Successfully unsubscribed from channel');
          } catch (error) {
            console.error('[Realtime] Error unsubscribing from channel:', error);
          } finally {
            supabaseChannelRef.current = null;
          }
        }
        
        // Clear the interaction timer on unmount
        if (interactionTimerRef.current) {
          clearTimeout(interactionTimerRef.current);
          interactionTimerRef.current = null;
        }
        
        // Reset remote update flag
        isRemoteUpdate.current = false;
      };
      
      cleanup();
    };
  }, [supabase]);

  const handleLocalChange = useCallback(
    (newElements: readonly ExcalidrawElement[], newAppState: AppState) => {
      try {
        // Skip if board isn't initialized yet
        if (!stateRef.current.isBoardInitialized) {
          console.log('[handleLocalChange] Board not initialized yet, skipping update');
          return;
        }
        
        // Skip processing if this is a remote update
        if (isRemoteUpdate.current) {
          console.log('[handleLocalChange] Skipping broadcast for remote update.');
          // Don't reset isRemoteUpdate here, let the effect that triggered it handle that
          return;
        }

        // Mark interaction start
        isInteractingRef.current = true;

        // Clear any existing interaction timer
        if (interactionTimerRef.current) {
          clearTimeout(interactionTimerRef.current);
        }

        // Set a timer to mark interaction end
        interactionTimerRef.current = setTimeout(() => {
          isInteractingRef.current = false;
          console.log('[Interaction] User has stopped interacting.');
        }, 300); // Reduced from 500ms for faster interaction detection

        // Sanitize and prepare the new state
        const sanitizedNewAppState = sanitizeAppState(newAppState);
        const sanitizedCurrentAppState = sanitizeAppState(stateRef.current.appState);

        // Skip if no meaningful changes
        if (_.isEqual(newElements, stateRef.current.elements) &&
            _.isEqual(sanitizedNewAppState, sanitizedCurrentAppState)) {
          return;
        }

        // Only log significant changes to reduce noise
        if (newElements.length !== stateRef.current.elements.length) {
          console.log(`[handleLocalChange] Elements changed: ${newElements.length} elements`);
        }

        // Update local ref with the latest state
        localBoardDataRef.current = { 
          elements: [...newElements], 
          appState: { ...newAppState } 
        };

        // Update local state immediately for better UX
        if (!_.isEqual(newElements, stateRef.current.elements)) {
          dispatch({ type: 'SET_ELEMENTS', payload: [...newElements] });
        }
        
        if (!_.isEqual(sanitizeAppState(newAppState), sanitizeAppState(stateRef.current.appState))) {
          dispatch({ type: 'SET_APP_STATE', payload: sanitizeAppState(newAppState) });
        }

        // Trigger the debounced broadcast
        broadcastSceneUpdate();
      } catch (error) {
        console.error('Error in handleLocalChange:', error);
      }
    },
    [broadcastSceneUpdate, sanitizeAppState]
  );

  const value = useMemo(
    () => ({
      ...state,
      joinRoom,
      leaveRoom,
      handleLocalChange,
    }),
    [state, joinRoom, leaveRoom, handleLocalChange]
  );

  return (
    <DrawingRoomContext.Provider value={value}>
      {children}
    </DrawingRoomContext.Provider>
  );
};

export const useDrawingRoom = () => {
  const context = useContext(DrawingRoomContext);
  if (!context) {
    throw new Error('useDrawingRoom must be used within a DrawingRoomProvider');
  }
  return context;
};